<template>
  <div class="an-page">
    <!-- header: chosen by the route name (requests-analysis) -->
    <MainHeader />
    <slot name="tabs" />

    <!-- KPIs -->
    <div class="an-kpis">
      <analysis-stat-card
        v-for="kpi in kpis"
        :key="kpi.label"
        v-bind="kpi"
      />
    </div>

    <!-- toolbar -->
    <div class="ra-toolbar">
      <div class="an-search">
        <feather-icon
          icon="SearchIcon"
          size="16"
        />
        <input
          v-model="search"
          type="text"
          placeholder="Search source..."
        >
      </div>

      <div class="ra-sort">
        <span class="ra-sort__label">
          <feather-icon
            icon="FilterIcon"
            size="14"
          />
          Sort by
        </span>
        <button
          v-for="option in sortOptions"
          :key="option.value"
          type="button"
          class="an-pill"
          :class="{ 'an-pill--active': sortBy === option.value }"
          @click="sortBy = option.value"
        >
          <feather-icon
            :icon="option.icon"
            size="13"
          />
          {{ option.label }}
        </button>
      </div>
    </div>

    <!-- source cards -->
    <div
      v-if="cards.length"
      class="ra-grid"
    >
      <div
        v-for="card in cards"
        :key="card.source"
        class="ra-card"
        :style="{ '--accent': card.theme.color, '--accent-soft': card.soft }"
        role="button"
        tabindex="0"
        @click="openDetails(card)"
        @keyup.enter="openDetails(card)"
      >
        <div class="ra-card__top">
          <div class="ra-card__icon">
            <feather-icon
              :icon="card.theme.icon"
              size="22"
            />
          </div>
          <div class="ra-card__title">
            <h5>{{ card.theme.label }}</h5>
            <span>{{ card.statusCount }} status types</span>
          </div>
          <span
            v-if="card.late > 0"
            class="ra-badge ra-badge--danger"
            title="Late requests"
          >
            <feather-icon
              icon="AlertTriangleIcon"
              size="12"
            />
            {{ format(card.late) }} late
          </span>
        </div>

        <div class="ra-card__total">
          <span class="ra-card__number">{{ format(card.total) }}</span>
          <span class="ra-card__caption">total requests</span>
        </div>

        <!-- stacked progress -->
        <div class="ra-progress">
          <div
            class="ra-progress__seg ra-progress__seg--done"
            :style="{ width: card.pct.issued + '%' }"
          />
          <div
            class="ra-progress__seg ra-progress__seg--pending"
            :style="{ width: card.pct.pending + '%' }"
          />
          <div
            class="ra-progress__seg ra-progress__seg--other"
            :style="{ width: card.pct.other + '%' }"
          />
        </div>
        <div class="ra-legend">
          <span><i class="ra-dot ra-dot--done" />Issued {{ card.pct.issued.toFixed(0) }}%</span>
          <span><i class="ra-dot ra-dot--pending" />Pending {{ card.pct.pending.toFixed(0) }}%</span>
          <span><i class="ra-dot ra-dot--other" />Other {{ card.pct.other.toFixed(0) }}%</span>
        </div>

        <div class="ra-mini">
          <div class="ra-mini__item">
            <feather-icon
              icon="AwardIcon"
              size="15"
              class="ra-icon ra-icon--done"
            />
            <strong>{{ format(card.issued) }}</strong>
            <small>Issued</small>
          </div>
          <div class="ra-mini__item">
            <feather-icon
              icon="ClockIcon"
              size="15"
              class="ra-icon ra-icon--pending"
            />
            <strong>{{ format(card.pending) }}</strong>
            <small>Pending</small>
          </div>
          <div class="ra-mini__item">
            <feather-icon
              icon="RepeatIcon"
              size="15"
              class="ra-icon ra-icon--turn"
            />
            <strong>{{ format(card.inTurn) }}</strong>
            <small>In turn</small>
          </div>
        </div>

        <div class="ra-card__footer">
          View breakdown
          <feather-icon
            icon="ChevronRightIcon"
            size="16"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="an-empty"
    >
      <feather-icon
        icon="InboxIcon"
        size="40"
      />
      <h5>No sources found</h5>
      <p>Try a different search term.</p>
    </div>

    <source-detail-modal
      :visible.sync="modal.visible"
      :source="modal.source"
      :subtitle="modal.subtitle"
      :data="modal.data"
    />
  </div>
</template>

<script>
import AnalysisStatCard from '@/components/analysis-table/AnalysisStatCard.vue'
import SourceDetailModal from '@/components/analysis-table/SourceDetailModal.vue'
import MainHeader from '@/components/main-header/MainHeader.vue'
import {
  getSourceTheme, splitAnalysis, tint, toNumber, formatNumber, palette,
} from '@/components/analysis-table/analysisTheme'

export default {
  components: {
    MainHeader,
    AnalysisStatCard,
    SourceDetailModal,
  },
  props: {
    requestsAnalysisData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      sortBy: 'total',
      sortOptions: [
        { value: 'total', label: 'Total', icon: 'BarChart2Icon' },
        { value: 'pending', label: 'Pending', icon: 'ClockIcon' },
        { value: 'late', label: 'Late', icon: 'AlertTriangleIcon' },
      ],
      modal: {
        visible: false, source: '', subtitle: '', data: {},
      },
    }
  },
  computed: {
    sources() {
      return (this.requestsAnalysisData || []).map(item => {
        const total = toNumber(item.total_count)
        const issued = toNumber(item.certificate_issued)
        const pending = toNumber(item.total_pending)
        const other = Math.max(total - issued - pending, 0)
        const base = total || 1

        return {
          source: item.source,
          theme: getSourceTheme(item.source),
          soft: tint(getSourceTheme(item.source).color, 0.13),
          total,
          issued,
          pending,
          late: toNumber(item.late_requests),
          inTurn: toNumber(item.in_turn),
          statusCount: splitAnalysis(item).breakdown.length,
          pct: {
            issued: Math.min((issued / base) * 100, 100),
            pending: Math.min((pending / base) * 100, 100),
            other: (other / base) * 100,
          },
          raw: item,
        }
      })
    },
    cards() {
      const query = this.search.trim().toLowerCase()
      const field = { total: 'total', pending: 'pending', late: 'late' }[this.sortBy]

      return this.sources
        .filter(s => !query || s.source.toLowerCase().includes(query) || s.theme.label.toLowerCase().includes(query))
        .sort((a, b) => b[field] - a[field])
    },
    kpis() {
      const sum = key => this.sources.reduce((acc, s) => acc + s[key], 0)

      return [
        {
          label: 'Total Requests', value: sum('total'), icon: 'BarChart2Icon', color: palette.primary, hint: `${this.sources.length} sources`,
        },
        {
          label: 'Certificates Issued', value: sum('issued'), icon: 'AwardIcon', color: palette.success,
        },
        {
          label: 'Total Pending', value: sum('pending'), icon: 'ClockIcon', color: palette.warning,
        },
        {
          label: 'Late Requests', value: sum('late'), icon: 'AlertTriangleIcon', color: palette.danger,
        },
      ]
    },
  },
  methods: {
    format: formatNumber,
    openDetails(card) {
      const { source, ...data } = card.raw
      this.modal = {
        visible: true,
        source: card.source,
        subtitle: `${formatNumber(card.total)} total requests`,
        data,
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/pages/analysis/analysis.scss';
</style>
